"use client";
import { memo, useMemo, type CSSProperties, type ReactElement } from "react";
import { ContainerPreset } from "@/constants/componentPresets";
import { ColorType } from "@/theme/colors";
import { CONTAINER_PRESET_CONFIGS } from "../Ccontainer/CcontainerPresets";
import { buildContainerStyle, resolveContainerStyleValues } from "../Ccontainer/CcontainerStyles";
import { HERO_TEAM_CARD_BACKGROUND } from "./constants";
import type { PlaceholderCardSlotProps } from "./types";
const PLACEHOLDER_CARD_RADIUS = 48;
const PLACEHOLDER_CARD_FILL_LENGTH = "100%";
const PLACEHOLDER_CARD_DISPLAY = "flex";
const PLACEHOLDER_CARD_BACKDROP_BLUR = 0;
const PLACEHOLDER_CARD_PADDING = 0;
const PLACEHOLDER_CARD_GLOW = 0;
const PLACEHOLDER_CARD_GLOSSY = 0;
const PLACEHOLDER_CARD_ANIMATION_DURATION_MS = 200;
const PLACEHOLDER_CARD_SURFACE_STYLE: CSSProperties = { backgroundColor: HERO_TEAM_CARD_BACKGROUND };
interface PlaceholderCardStyleInput {
    preset?: ContainerPreset;
}
/**
 * Off-screen slots outnumber rendered cards by ~20:1, so the placeholder surface
 * is built from Ccontainer's own pure style builders instead of mounting a
 * Ccontainer. Identical preset resolution and identical CSS — without the four
 * container hook models, the root's telemetry layout effect, and the extra
 * fibers (container root, content wrapper, two debug path boundaries) that a
 * childless, non-interactive rectangle has no use for.
 */
function buildPlaceholderCardStyle({ preset = ContainerPreset.Default }: PlaceholderCardStyleInput): CSSProperties {
    const styleValues = resolveContainerStyleValues({
        presetConfig: CONTAINER_PRESET_CONFIGS[preset],
        radius: PLACEHOLDER_CARD_RADIUS,
        backdropBlur: PLACEHOLDER_CARD_BACKDROP_BLUR,
        padding: PLACEHOLDER_CARD_PADDING,
        width: PLACEHOLDER_CARD_FILL_LENGTH,
        height: PLACEHOLDER_CARD_FILL_LENGTH,
        glow: PLACEHOLDER_CARD_GLOW,
        disableHoverShadow: false,
        isHovered: false,
    });
    return buildContainerStyle({
        values: styleValues,
        display: PLACEHOLDER_CARD_DISPLAY,
        glowColor: ColorType.Accent,
        glossy: PLACEHOLDER_CARD_GLOSSY,
        innerBorder: true,
        useGradientBorder: styleValues.finalBorderGradient != null,
        useGradientBackground: styleValues.finalBackgroundGradient != null,
        hasGlowLayer: false,
        isLoadingActive: false,
        smooth: true,
        animationDuration: PLACEHOLDER_CARD_ANIMATION_DURATION_MS,
        clickable: true,
        style: PLACEHOLDER_CARD_SURFACE_STYLE,
    });
}
export const CarouselPlaceholderSlot = memo(function CarouselPlaceholderSlot({ cardWidth, cardHeight, cardGap, cardContainerPreset, }: PlaceholderCardSlotProps): ReactElement {
    const slotStyle = useMemo<CSSProperties>(() => ({
        flexShrink: 0,
        width: cardWidth,
        height: cardHeight,
        marginRight: cardGap,
        scrollSnapAlign: "center",
        scrollSnapStop: "always",
    }), [cardGap, cardHeight, cardWidth]);
    const cardStyle = useMemo<CSSProperties>(() => buildPlaceholderCardStyle({ preset: cardContainerPreset }), [cardContainerPreset]);
    return <div data-el="Cteamscarousel/CarouselPlaceholderSlot/Root" style={slotStyle}>
            <div style={cardStyle}/>
        </div>
        ;
});
